import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check, Mail, MapPin, Clock } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["Federal Civilian", "DoD", "Intel", "State & Local", "Healthcare", "Enterprise", "Mid-Market/SMB"];
const INTERESTS = ["DevSecOps", "Cloud", "Platform / Kubernetes", "Cybersecurity", "AI/ML", "PMO", "Advisory"];

const initial = { full_name: "", organization: "", email: "", phone: "", sector: "", interest: "", requirements: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Please check your entries and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Contact — Initiate consultation" description="Brief the Gravitas Technology Solutions engineering team. Washington DC. One business day response SLA." path="/contact" />

      <section style={{ paddingTop: 120, paddingBottom: 64 }}>
        <div className="container-grav">
          <div className="eyebrow" style={{ marginBottom: 28 }}><span className="sep" />Contact</div>
          <h1 className="display-xl" style={{ maxWidth: 1200 }}>
            Let's start a <span style={{ color: "var(--green)", fontStyle: "italic", fontWeight: 800 }}>conversation.</span>
          </h1>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 40 }}>
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <aside className="lg:col-span-4">
              <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Headquarters</div>
              <h2 className="display-md">Washington, DC · DMV</h2>
              <p className="lede" style={{ marginTop: 20, color: "var(--muted)" }}>Serving federal agencies and enterprise clients nationally.</p>

              <div style={{ marginTop: 48, display: "flex", flexDirection: "column", gap: 24 }}>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MapPin size={18} /></div>
                  <div>
                    <div className="eyebrow">Location</div>
                    <div style={{ marginTop: 6, fontSize: 16 }}>Washington, DC · DMV</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Mail size={18} /></div>
                  <div>
                    <div className="eyebrow">Email</div>
                    <a href="mailto:info@gravitastechnology.com" style={{ color: "var(--ink)", textDecoration: "none", fontSize: 16 }}>info@gravitastechnology.com</a>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Clock size={18} /></div>
                  <div>
                    <div className="eyebrow">Response SLA</div>
                    <div style={{ marginTop: 6, fontSize: 16 }}>1 business day</div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 56, borderTop: "1px solid var(--line)", paddingTop: 32 }}>
                <div className="eyebrow" style={{ marginBottom: 14 }}>Compliance</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {["NIST 800-53", "FEDRAMP", "CMMC 2.0", "RMF", "ZERO TRUST"].map((b) => (
                    <span key={b} className="chip" style={{ padding: "6px 14px", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>{b}</span>
                  ))}
                </div>
              </div>
            </aside>

            <div className="lg:col-span-8">
              {status === "success" ? (
                <div data-testid="contact-success" className="tile" style={{ padding: "64px 48px", background: "var(--green-bg)", border: "1.5px solid var(--green)" }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--green)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    <Check size={26} strokeWidth={2.5} />
                  </div>
                  <h3 className="display-md" style={{ marginTop: 28 }}>Request received.</h3>
                  <p className="lede" style={{ marginTop: 14, color: "var(--muted)" }}>Our team will respond within one business day.</p>
                  <button className="btn btn-ghost" style={{ marginTop: 36 }} onClick={() => setStatus(null)} data-testid="contact-new">
                    Submit another request <span className="arrow"><ArrowUpRight size={14} /></span>
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" style={{ display: "flex", flexDirection: "column", gap: 40 }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <label className="eyebrow">Full name*</label>
                      <input required className="input" value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" data-testid="input-full-name" />
                    </div>
                    <div>
                      <label className="eyebrow">Organization*</label>
                      <input required className="input" value={form.organization} onChange={set("organization")} placeholder="Agency or company" data-testid="input-organization" />
                    </div>
                    <div>
                      <label className="eyebrow">Email*</label>
                      <input required type="email" className="input" value={form.email} onChange={set("email")} placeholder="you@agency.gov" data-testid="input-email" />
                    </div>
                    <div>
                      <label className="eyebrow">Phone</label>
                      <input className="input" value={form.phone} onChange={set("phone")} placeholder="(202) 555-0100" data-testid="input-phone" />
                    </div>
                  </div>

                  <div>
                    <label className="eyebrow">Sector</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
                      {SECTORS.map((s) => (
                        <button type="button" key={s} className={`chip ${form.sector === s ? "active" : ""}`} onClick={() => setForm((f) => ({ ...f, sector: f.sector === s ? "" : s }))} data-testid={`chip-sector-${s.toLowerCase().replace(/[\s/&]+/g, "-")}`}>{s}</button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="eyebrow">Area of interest</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
                      {INTERESTS.map((s) => (
                        <button type="button" key={s} className={`chip ${form.interest === s ? "active" : ""}`} onClick={() => setForm((f) => ({ ...f, interest: f.interest === s ? "" : s }))} data-testid={`chip-interest-${s.toLowerCase().replace(/[\s/&]+/g, "-")}`}>{s}</button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="eyebrow">Requirements*</label>
                    <textarea required className="input" style={{ marginTop: 16 }} rows={6} value={form.requirements} onChange={set("requirements")} placeholder="Describe your mission context, timeline, and desired outcomes." data-testid="input-requirements" />
                  </div>

                  {status === "error" && (
                    <div data-testid="contact-error" style={{ color: "#B91C1C", fontSize: 14, border: "1px solid #FCA5A5", padding: "14px 18px", background: "#FEF2F2" }}>
                      {error || "Submission failed. Please try again."}
                    </div>
                  )}

                  <div>
                    <button type="submit" disabled={submitting} className="btn" data-testid="contact-submit">
                      {submitting ? "Submitting..." : "Submit consultation request"}
                      <span className="arrow"><ArrowUpRight size={14} strokeWidth={2.5} /></span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
